import { ModalButtonStyles, OpenModal, useTable } from '@composables/common'
import { NameAndId } from '@src/components'
import { ContentDocument } from '@src/types'
import { UploadFileResponse } from '@utils/api/s3'
import { useUserStore } from '@utils/stores/user'
import { Ref, computed, h } from 'vue'
import { useI18n } from 'vue-i18n'

export function useCampaignContentDocumentsTable(
    contentDocuments: Ref<ContentDocument[]>,
    update: (successMessage?: string | null) => Promise<void>,
    openModal: OpenModal,
    isModalOpen: Ref<boolean>
) {
    const userStore = useUserStore()
    const i18n = useI18n()

    const total = computed(() => {
        return contentDocuments.value.length
    })

    const {
        TableWrapper: CampaignContentDocumentsTable,
        selected: selectedContentDocumentsIds,
    } = useTable({
        tableProps: {
            id: 'content_documents_table',
            label: 'content_documents',
            help: 'help_campaign_content_documents_table',
            data: contentDocuments,
            total: total,
            columns: [
                {
                    label: 'id',
                    key: 'uuid',
                    select: true,
                },
                {
                    label: 'id',
                    key: 'uuid',
                    sorting: true,
                    url: contentDocumentsTableUrl,
                    targetBlank: true,
                    condition: userStore.can('see_id_columns'),
                },
                {
                    label: 'name',
                    key: 'name',
                    sorting: true,
                    url: contentDocumentsTableUrl,
                    targetBlank: true,
                },
            ],
            actions: [
                {
                    action: handleViewContentDocument,
                    icon: 'fa-eye',
                    title: 'view',
                },
                {
                    action: handleDeleteContentDocument,
                    icon: 'fas fa-trash-alt',
                    title: 'delete',
                    color: 'text-danger-500',
                    condition: userStore.can('update_campaign'),
                },
            ],
        },
    })

    function contentDocumentsTableUrl(contentDocument: ContentDocument) {
        return contentDocument.url as string
    }

    function handleViewContentDocument(contentDocument: ContentDocument) {
        const url = contentDocumentsTableUrl(contentDocument)
        if (url) {
            window.open(url)
        }
    }

    function handleDeleteContentDocument(contentDocument: ContentDocument) {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_campaign_content_document',
                title: 'modal_title_delete_campaign_content_document',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deleteContentDocument(contentDocument),
            },
            () =>
                h(NameAndId, {
                    idKey: 'uuid',
                    nameKey: 'name',
                    modelValue: [contentDocument],
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function handleDeleteContentDocuments() {
        isModalOpen.value = true
        openModal(
            {
                id: 'delete_campaign_content_documents',
                title: 'modal_title_delete_campaign_content_documents',
                open: isModalOpen,
                onCancel: () => (isModalOpen.value = false),
                onSubmit: () => deleteContentDocuments(),
            },
            () =>
                h(NameAndId, {
                    idKey: 'uuid',
                    nameKey: 'name',
                    modelValue: selectedContentDocuments.value,
                }),
            {
                submitButtonText: 'delete',
                styles: {
                    submitButton: ModalButtonStyles['RED'],
                },
            }
        )
    }

    function deleteContentDocument(
        contentDocumentToDelete: ContentDocument,
        submit = true
    ) {
        contentDocuments.value.splice(
            contentDocuments.value.findIndex(
                (contentDocument) =>
                    contentDocumentToDelete.uuid == contentDocument.uuid
            ),
            1
        )
        if (submit) {
            update(
                i18n.t('toast_success_campaign_content_document_deleted', {
                    contentDocument: contentDocumentToDelete.name,
                })
            )
            isModalOpen.value = false
        }
    }

    function deleteContentDocuments() {
        selectedContentDocuments.value.forEach((contentDocument) => {
            deleteContentDocument(contentDocument, false)
        })
        update(
            i18n.t('toast_success_campaign_content_documents_deleted', {
                contentDocuments: selectedContentDocuments.value
                    .map((contentDocument) => contentDocument.name)
                    .join(', '),
            })
        )
        selectedContentDocumentsIds.value = []
        isModalOpen.value = false
    }

    const selectedContentDocuments = computed(() => {
        return contentDocuments.value.filter(
            (item: ContentDocument) =>
                item.uuid &&
                selectedContentDocumentsIds.value.includes(item.uuid)
        )
    })

    function handleCreateContentDocument(
        response: UploadFileResponse,
        fileName: string
    ) {
        const newDocument = new ContentDocument()
        newDocument.upload_key = response.key
        newDocument.url = response.temp_storage_url
        contentDocuments.value.push(newDocument)
        update(
            i18n.t('toast_success_campaign_content_document_created', {
                contentDocument: fileName,
            })
        )
    }

    return {
        CampaignContentDocumentsTable,
        selectedContentDocumentsIds,
        handleDeleteContentDocuments,
        handleCreateContentDocument,
    }
}
